import React, { Fragment } from 'react';
import { useSnackbar } from 'notistack';
import { Dialog, Transition, Listbox } from '@headlessui/react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { SelectorIcon, CheckIcon } from '@heroicons/react/outline';
import SubmitButton from 'components/common/SubmitButton';
import { request } from 'apis/tenants';
import { useStoreState } from 'easy-peasy';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const types = [
  { value: 'Technical Support', label: 'Technical Support' },
  { value: 'Licensing and Billing', label: 'Licensing and Billing' },
  { value: 'Product Trial Questions', label: 'Product Trial Questions' },
  { value: 'Report a Bug', label: 'Report a Bug' },
  { value: 'Feature Suggestion', label: 'Feature Suggestion' },
  { value: 'Other', label: 'Other' },
];

export function HelpCentre({ open, setOpen }) {
  const { enqueueSnackbar } = useSnackbar();
  const platform = useStoreState((state) => state.platform);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" auto-reopen="true" className="fixed z-10 inset-0 overflow-y-auto" onClose={setOpen}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6">
              <div>
                <div className="mt-3">
                  <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                    <div className="flex flex-1 justify-center">
                      <span>Help Centre</span>
                    </div>
                  </Dialog.Title>
                  <Formik
                    initialValues={{
                      type: {},
                      request: '',
                    }}
                    validationSchema={Yup.object().shape({
                      type: Yup.object({
                        value: Yup.string().required('Required'),
                        label: Yup.string().required('Required'),
                      }),
                      request: Yup.string().required('Required'),
                    })}
                    onSubmit={(values, bag) => {
                      const form = {
                        type: values.type.value,
                        request: values.request,
                      };
                      request(platform.tenantId, form)
                        .then(({ status }) => {
                          if (status === 200) {
                            enqueueSnackbar('Your request has been sent.', { variant: 'success' });
                            bag.resetForm();
                          }
                        })
                        .finally(() => {
                          bag.setSubmitting(false);
                          setOpen(false);
                        });
                    }}
                  >
                    {(pps) => {
                      const {
                        values,
                        errors,
                        setFieldValue,
                        handleChange,
                        handleSubmit,
                        isSubmitting,
                      } = pps;
                      
                      return (
                        <div className="mt-2 grid grid-cols-12 gap-6">
                          <div className="col-span-12">
                            <Listbox value={values.type} onChange={(value) => setFieldValue('type', value)}>
                              {({ open }) => (
                                <>
                                  <Listbox.Label className="block text-sm font-medium text-gray-700">Type</Listbox.Label>
                                  <div className="mt-1 relative">
                                    <Listbox.Button
                                      className={
                                        classNames(
                                          errors.type ? 'border-red-500' : '',
                                          "bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                                        )
                                      }
                                    >
                                      <span className="block truncate">{values.type.label ? values.type.label : 'Select Your Request Type'}</span>
                                      <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                        <SelectorIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                      </span>
                                    </Listbox.Button>

                                    <Transition
                                      show={open}
                                      as={Fragment}
                                      leave="transition ease-in duration-100"
                                      leaveFrom="opacity-100"
                                      leaveTo="opacity-0"
                                    >
                                      <Listbox.Options className="z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                        {types.map((type) => (
                                          <Listbox.Option
                                            key={type.value}
                                            className={({ active }) =>
                                              classNames(
                                                active ? 'text-white bg-sky-600' : 'text-gray-900',
                                                'cursor-default select-none relative py-2 pl-3 pr-9'
                                              )
                                            }
                                            value={type}
                                          >
                                            {({ selected, active }) => (
                                              <>
                                                <span className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                                                  {type.label}
                                                </span>

                                                {selected ? (
                                                  <span
                                                    className={classNames(
                                                      active ? 'text-white' : 'text-sky-600',
                                                      'absolute inset-y-0 right-0 flex items-center pr-4'
                                                    )}
                                                  >
                                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                  </span>
                                                ) : null}
                                              </>
                                            )}
                                          </Listbox.Option>
                                        ))}
                                      </Listbox.Options>
                                    </Transition>
                                  </div>
                                </>
                              )}
                            </Listbox>
                            { errors.type && (
                              <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                {errors.type.value}
                              </span>
                            ) }
                          </div>
                          <div className="col-span-12">
                            <label htmlFor="request" className="block text-sm font-medium text-gray-700">
                              Request
                            </label>
                            <textarea
                              name="request"
                              rows={3}
                              className={
                                classNames(
                                  errors.request ? 'border-red-500' : '',
                                  "max-w-lg shadow-sm block w-full focus:ring-sky-500 focus:border-sky-500 sm:text-sm border border-gray-300 rounded-md py-2 px-3 mt-1"
                                )
                              }
                              value={values.request}
                              onChange={handleChange}
                            />
                            { errors.request && (
                              <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                {errors.request}
                              </span>
                            ) }
                          </div>
                          <div className="col-span-12 flex justify-end">
                            <SubmitButton
                              label="Submit"
                              onClick={handleSubmit}
                              isSubmitting={isSubmitting}
                              isSubmittingText="Submitting"
                            />
                          </div>
                        </div>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}